import React, { useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Alert,
  Chip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Delete } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RateType } from 'api/consumablesApi';
import ConfirmDeleteDialog from 'components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import { blue, grey } from '@mui/material/colors';
import { blueGrey } from '@material-ui/core/colors';
import {
  deleteEquipmentBuyRate,
  deleteEquipmentSellRate,
  EquipmentBuyAndSellRateType,
} from 'api/equipmentApi';
import { useSnackbar } from 'notistack';
import { currencyType } from 'api/organizationApi';

interface DetailsViewTableRowProps {
  row: { key: string; rates: EquipmentBuyAndSellRateType[] };
  type?: RateType;
}

function DetailsViewTableRow({ row, type }: DetailsViewTableRowProps) {
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] =
    useState<EquipmentBuyAndSellRateType | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const deleteEquipmentSellRateMutation = useMutation(deleteEquipmentSellRate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['equipment-rate-items']);
      enqueueSnackbar('Sell rate deleted successfully', { variant: 'success' });
    },
    onError: (error) => {
      console.error('Error deleting sell rate:', error);
      enqueueSnackbar('Failed to delete sell rate', { variant: 'error' });
    },
  });

  const deleteEquipmentBuyRateMutation = useMutation(deleteEquipmentBuyRate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['equipment-rate-items']);
      enqueueSnackbar('Buy rate deleted successfully', { variant: 'success' });
    },
    onError: (error) => {
      console.error('Error deleting buy rate:', error);
      enqueueSnackbar('Failed to delete buy rate', { variant: 'error' });
    },
  });

  const handleDelete = async () => {
    if (!selectedItem) return;
    if (type === RateType.SellRate) {
      deleteEquipmentSellRateMutation.mutate(selectedItem.id);
    } else {
      deleteEquipmentBuyRateMutation.mutate(selectedItem.id);
    }
    setDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  const openDeleteDialog = (item: EquipmentBuyAndSellRateType) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <Box display="flex" alignItems="center">
            <IconButton
              aria-label="expand row"
              size="medium"
              onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>

            {row.key.split('/').map((part, index) => (
              <Chip
                key={`${row.key}-${index}`}
                label={part}
                variant="outlined"
                sx={{
                  margin: '2px',
                  padding: '4px 4px',
                  color: '#000',
                  // backgroundColor:
                  //   type === RateType.BuyRate ? blueGrey[200] : blue[200],
                  paddingInline: '0.5rem',
                  paddingY: '1rem',
                  borderColor:
                    type === RateType.BuyRate ? blueGrey[200] : blue[200],
                }}
                color="primary"
                size="small"
                icon={index === 0 ? <BusinessIcon /> : <AccountBoxIcon />}
              />
            ))}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table size="small" aria-label="history">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ border: 'none' }}>
                      Effective From
                    </TableCell>
                    <TableCell sx={{ border: 'none' }}>
                      {type === RateType.SellRate ? 'Sell Price' : 'Buy Price'}
                    </TableCell>
                    <TableCell sx={{ border: 'none' }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.rates.map((item) => (
                    <TableRow key={item?.equipmentItemId}>
                      <TableCell sx={{ border: 'none' }}>
                        {new Date(item.effectiveFrom).toDateString()}
                      </TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        {currencyType}
                        {Number(
                          type === RateType.SellRate
                            ? item.sellRate
                            : item.buyRate
                        ).toFixed(2) || '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ border: 'none' }}>
                        {/* <Button
                          size="small"
                          variant="outlined"
                          sx={{ marginRight: '0.5rem' }}
                          startIcon={<EditIcon />}
                          onClick={() => {
                            handleOpenEditRatesDialog(item);
                          }}>
                          Edit
                        </Button> */}
                        <IconButton onClick={() => openDeleteDialog(item)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {deleteDialogOpen && selectedItem && (
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          title="Delete Confirmation"
          content={
            <>
              Are you sure you want to delete the item with the effective date{' '}
              <b>{new Date(selectedItem.effectiveFrom).toDateString()}</b>?
              <Alert severity="warning" style={{ marginTop: '1rem' }}>
                This action is irreversible and will permanently delete the
                data.
              </Alert>
            </>
          }
          deleteFunc={handleDelete} // Pass the delete function here
          handleReject={() => {
            setDeleteDialogOpen(false);
            setSelectedItem(null);
          }}
          handleClose={() => {
            setDeleteDialogOpen(false);
            setSelectedItem(null);
          }}
        />
      )}
    </>
  );
}

interface EquipmentSellAndBuyDetailsViewTableProps {
  equipment: { key: string; rates: EquipmentBuyAndSellRateType[] }[];
  type: 'sell' | 'buy';
}
export default function EquipmentSellAndBuyDetailsViewTable({
  equipment,
  type,
}: EquipmentSellAndBuyDetailsViewTableProps) {
  return (
    <Box>
      <Typography variant="h6" sx={{ fontSize: '1.2rem', pl: 1 }} gutterBottom>
        {type === 'sell' ? 'Sell Rates ' : 'Buy Rates'}
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ border: '1px solid', borderColor: grey[300], borderRadius: 2 }}
        elevation={0}>
        <Table>
          <TableHead sx={{ backgroundColor: grey[50] }}>
            <TableRow>
              <TableCell colSpan={3}>Branch / Client</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {equipment && equipment.length > 0 ? (
              equipment.map((row, index) => (
                <DetailsViewTableRow
                  key={index}
                  row={row}
                  type={type === 'sell' ? RateType.SellRate : RateType.BuyRate}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No data available.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
